import { useState, useRef, useMemo } from "react";
import type { Contact, ContactEntityKind, EmailLijstKey } from "@domaddress/shared";
import { hasContactCardIdentity, isOrganizationContactKind } from "@domaddress/shared";
import { api } from "../lib/api";
import IsoDateInput from "./IsoDateInput";
import { EXTERNE_CONTACTEN_API, LEVERANCIERS_API, ALLE_CONTACTEN_API } from "../lib/contactApiPaths";

interface Props {
  contact: Contact | null;
  onClose: () => void;
  onSaved: () => void;
  /** Zonder trailing slash, bijv. /api/medewerkers */
  apiPath: string;
  titleNieuw: string;
  titleBewerken: string;
}

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3000";

const CATEGORIE_OPTIES: { value: ContactEntityKind; label: string }[] = [
  { value: "personeel",        label: "Personeel" },
  { value: "vrijwilliger",     label: "Vrijwilliger" },
  { value: "dominicusganger",  label: "Dominicusganger" },
  { value: "extern",           label: "Extern contact" },
  { value: "leverancier",      label: "Leverancier" },
  { value: "emaillijst",       label: "E-mailabonnee" },
];

/** Meerdere lijsten tegelijk; "geen" = niets aangevinkt. */
const emailLijstCheckboxOptions: { value: Exclude<EmailLijstKey, "geen">; label: string }[] = [
  { value: "alle_email", label: "Allen met e-mail" },
  { value: "informatief", label: "Informatieve berichten" },
  { value: "overlijden", label: "Bericht bij overlijden" },
  { value: "40_dagen", label: "40-dagen-mailing" },
];

function initialEmailLijsten(c: Contact | null): Exclude<EmailLijstKey, "geen">[] {
  const raw = c?.email_lijsten?.length
    ? c.email_lijsten
    : c?.email_lijst && c.email_lijst !== "geen"
      ? [c.email_lijst]
      : [];
  const allowed = new Set(emailLijstCheckboxOptions.map((o) => o.value));
  return [...new Set(raw.filter((k): k is Exclude<EmailLijstKey, "geen"> => allowed.has(k as Exclude<EmailLijstKey, "geen">)))];
}

export default function ContactFormModal({
  contact,
  onClose,
  onSaved,
  apiPath,
  titleNieuw,
  titleBewerken,
}: Props) {
  const isEdit = !!contact;
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(
    contact?.profile_picture ? `${API_URL}${contact.profile_picture}` : null
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    first_name: contact?.first_name ?? "",
    last_name: contact?.last_name ?? "",
    tussenvoegsel: contact?.tussenvoegsel ?? "",
    initialen: contact?.initialen ?? "",
    email: contact?.email_prive || contact?.email || "",
    email_werk: contact?.email_werk ?? "",
    dominicus_email: contact?.dominicus_email ?? "",
    email_lijsten: initialEmailLijsten(contact),
    phone: contact?.phone ?? "",
    phone_mobile: contact?.phone_mobile ?? "",
    address: contact?.address ?? "",
    city: contact?.city ?? "",
    postal_code: contact?.postal_code ?? "",
    country: contact?.country ?? "",
    notitie: contact?.notitie ?? "",
    notities: contact?.notities || contact?.notes || "",
    extra: contact?.extra ?? "",
    birth_date: contact?.birth_date ?? "",
    death_date: contact?.death_date ?? "",
    kwalificaties: contact?.kwalificaties ?? "",
    bedrijfsnaam: contact?.bedrijfsnaam ?? "",
    bellijn: contact?.bellijn ?? false,
    berichten: contact?.berichten ?? false,
    agv_ingestemd: contact?.agv_ingestemd ?? false,
    entity_kind: (contact?.entity_kind ?? (apiPath === ALLE_CONTACTEN_API ? "vrijwilliger" : "")) as ContactEntityKind | "",
  });

  const showBedrijfsnaam = useMemo(
    () =>
      apiPath === EXTERNE_CONTACTEN_API ||
      apiPath === LEVERANCIERS_API ||
      form.entity_kind === "extern" ||
      form.entity_kind === "leverancier",
    [apiPath, form.entity_kind]
  );

  const isOrgForm = useMemo(() => {
    if (apiPath === EXTERNE_CONTACTEN_API || apiPath === LEVERANCIERS_API) return true;
    if (apiPath === ALLE_CONTACTEN_API && isOrganizationContactKind(form.entity_kind)) return true;
    return isOrganizationContactKind(contact?.entity_kind);
  }, [apiPath, form.entity_kind, contact?.entity_kind]);

  const nameFieldsRequired = !isOrgForm;

  function handleFieldChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) {
    const t = e.target;
    if (t.type === "checkbox") {
      setForm({ ...form, [t.name]: (t as HTMLInputElement).checked });
    } else {
      setForm({ ...form, [t.name]: t.value });
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      setPreview(URL.createObjectURL(file));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (
      isOrgForm &&
      !hasContactCardIdentity({
        first_name: form.first_name,
        last_name: form.last_name,
        bedrijfsnaam: form.bedrijfsnaam,
        email: form.email,
        email_prive: form.email,
        email_werk: form.email_werk,
        dominicus_email: form.dominicus_email,
        phone: form.phone,
        phone_mobile: form.phone_mobile,
      })
    ) {
      setError(
        "Vul minstens voornaam, achternaam, bedrijfsnaam, e-mail of telefoon in — u hoeft niet per se een volledige persoonsnaam te hebben."
      );
      return;
    }
    setSaving(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("first_name", form.first_name);
      formData.append("last_name", form.last_name);
      formData.append("tussenvoegsel", form.tussenvoegsel);
      formData.append("initialen", form.initialen);
      formData.append("email", form.email);
      formData.append("email_prive", form.email);
      formData.append("email_werk", form.email_werk);
      formData.append("dominicus_email", form.dominicus_email);
      formData.append("email_lijsten", JSON.stringify(form.email_lijsten));
      formData.append("phone", form.phone);
      formData.append("phone_mobile", form.phone_mobile);
      formData.append("address", form.address);
      formData.append("city", form.city);
      formData.append("postal_code", form.postal_code);
      formData.append("country", form.country);
      formData.append("notitie", form.notitie);
      formData.append("notities", form.notities);
      formData.append("extra", form.extra);
      formData.append("birth_date", form.birth_date);
      formData.append("death_date", form.death_date);
      formData.append("kwalificaties", form.kwalificaties);
      formData.append("bedrijfsnaam", form.bedrijfsnaam);
      formData.append("bellijn", form.bellijn ? "1" : "0");
      formData.append("berichten", form.berichten ? "1" : "0");
      formData.append("agv_ingestemd", form.agv_ingestemd ? "1" : "0");
      if (form.entity_kind) {
        formData.append("entity_kind", form.entity_kind);
      }
      const file = fileRef.current?.files?.[0];
      if (file) formData.append("profile_picture", file);

      if (isEdit) {
        await api.put(`${apiPath}/${contact.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post(apiPath, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      onSaved();
    } catch (err: unknown) {
      const ex = err as { response?: { data?: { error?: string } } };
      setError(ex.response?.data?.error ?? "Er ging iets mis");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? titleBewerken : titleNieuw}</h2>
          <button className="modal-close" onClick={onClose} type="button">
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">
          <div className="avatar-upload-section">
            <div
              className="avatar-upload"
              onClick={() => fileRef.current?.click()}
              onKeyDown={(e) => e.key === "Enter" && fileRef.current?.click()}
              role="button"
              tabIndex={0}
            >
              {preview ? (
                <img src={preview} alt="Voorbeeld" />
              ) : (
                <span className="avatar-placeholder">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="8" r="5" />
                    <path d="M20 21a8 8 0 1 0-16 0" />
                  </svg>
                </span>
              )}
              <div className="avatar-edit-overlay">📷</div>
            </div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFileChange} />
            <span className="avatar-hint">Klik om een foto te uploaden</span>
          </div>

          {isEdit && (
            <div className="form-group">
              <label>Primaire lijst</label>
              <select
                name="entity_kind"
                value={form.entity_kind}
                onChange={handleFieldChange}
              >
                {CATEGORIE_OPTIES.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <span style={{ fontSize: "0.78rem", color: "var(--text-muted, #888)", marginTop: "0.25rem", display: "block" }}>
                De primaire lijst van dit contact. Je kunt extra lijsten instellen in de detailweergave.
              </span>
            </div>
          )}

          {!isEdit && apiPath === ALLE_CONTACTEN_API && (
            <div className="form-group">
              <label>Primaire lijst *</label>
              <select
                name="entity_kind"
                required
                value={form.entity_kind}
                onChange={handleFieldChange}
              >
                {CATEGORIE_OPTIES.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <span style={{ fontSize: "0.78rem", color: "var(--text-muted, #888)", marginTop: "0.25rem", display: "block" }}>
                Waar dit contact hoofdzakelijk hoort (vrijwilliger, personeel, …).
              </span>
            </div>
          )}

          {showBedrijfsnaam && (
            <div className="form-group">
              <label>Bedrijfsnaam</label>
              <input
                name="bedrijfsnaam"
                value={form.bedrijfsnaam}
                onChange={handleFieldChange}
                placeholder={
                  isOrgForm
                    ? "Organisatie of bedrijf (mag ook zonder voornaam/achternaam)"
                    : "Organisatie of bedrijf"
                }
                maxLength={200}
              />
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label>{nameFieldsRequired ? "Voornaam *" : "Voornaam"}</label>
              <input
                name="first_name"
                required={nameFieldsRequired}
                value={form.first_name}
                onChange={handleFieldChange}
              />
            </div>
            <div className="form-group">
              <label>Tussenvoegsel</label>
              <input name="tussenvoegsel" value={form.tussenvoegsel} onChange={handleFieldChange} />
            </div>
            <div className="form-group">
              <label>{nameFieldsRequired ? "Achternaam *" : "Achternaam"}</label>
              <input
                name="last_name"
                required={nameFieldsRequired}
                value={form.last_name}
                onChange={handleFieldChange}
              />
            </div>
          </div>
          {isOrgForm && (
            <p className="page-subtitle" style={{ marginTop: 0, marginBottom: "1rem" }}>
              {apiPath === LEVERANCIERS_API
                ? "Bij leveranciers is een volledige persoonsnaam niet verplicht."
                : apiPath === EXTERNE_CONTACTEN_API
                  ? "Bij externe contacten is een volledige persoonsnaam niet verplicht."
                  : "Bij externe contacten en leveranciers is een volledige persoonsnaam niet verplicht."}{" "}
              Vul minstens bedrijfsnaam, e-mail of telefoon in als de naam onbekend is.
            </p>
          )}
          <div className="form-row">
            <div className="form-group">
              <label>Initialen</label>
              <input name="initialen" value={form.initialen} onChange={handleFieldChange} />
            </div>
            <div className="form-group">
              <label>E-mail (primair)</label>
              <input name="email" type="email" value={form.email} onChange={handleFieldChange} />
            </div>
            <div className="form-group">
              <label>E-mail (secundair)</label>
              <input name="email_werk" type="email" value={form.email_werk} onChange={handleFieldChange} />
            </div>
          </div>
          <div className="form-group">
            <label>E-mail Dominicus (eigen domein)</label>
            <input name="dominicus_email" type="email" value={form.dominicus_email} onChange={handleFieldChange} />
            <span style={{ fontSize: "0.78rem", color: "var(--text-muted, #888)", marginTop: "0.25rem", display: "block" }}>
              Bijvoorbeeld <strong>@dominicusamsterdam.nl</strong> — niet in primair of secundair vullen.
            </span>
          </div>
          <div className="form-group">
            <span className="form-group-label" style={{ display: "block", marginBottom: "0.35rem" }}>
              E-mail lijsten (meerdere mogelijk)
            </span>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
              {emailLijstCheckboxOptions.map((o) => (
                <label key={o.value} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: "normal" }}>
                  <input
                    type="checkbox"
                    checked={form.email_lijsten.includes(o.value)}
                    onChange={() => {
                      setForm((f) => ({
                        ...f,
                        email_lijsten: f.email_lijsten.includes(o.value)
                          ? f.email_lijsten.filter((x) => x !== o.value)
                          : [...f.email_lijsten, o.value],
                      }));
                    }}
                  />
                  {o.label}
                </label>
              ))}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Telefoon (primair)</label>
              <input name="phone" type="tel" value={form.phone} onChange={handleFieldChange} placeholder="Vast of algemeen" />
            </div>
            <div className="form-group">
              <label>Telefoon (secundair)</label>
              <input name="phone_mobile" type="tel" value={form.phone_mobile} onChange={handleFieldChange} placeholder="Mobiel 06…" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ alignSelf: "end" }}>
              <label>
                <input
                  name="bellijn"
                  type="checkbox"
                  checked={form.bellijn}
                  onChange={handleFieldChange}
                />{" "}
                Bellijn
              </label>
            </div>
            <div className="form-group" style={{ alignSelf: "end" }}>
              <label>
                <input
                  name="berichten"
                  type="checkbox"
                  checked={form.berichten}
                  onChange={handleFieldChange}
                />{" "}
                Berichten
              </label>
            </div>
            <div className="form-group" style={{ alignSelf: "end" }}>
              <label>
                <input
                  name="agv_ingestemd"
                  type="checkbox"
                  checked={form.agv_ingestemd}
                  onChange={handleFieldChange}
                />{" "}
                AVG / voorwaarden ingestemd
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>Straat</label>
            <input name="address" value={form.address} onChange={handleFieldChange} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Postcode</label>
              <input name="postal_code" value={form.postal_code} onChange={handleFieldChange} />
            </div>
            <div className="form-group">
              <label>Woonplaats</label>
              <input name="city" value={form.city} onChange={handleFieldChange} />
            </div>
            <div className="form-group">
              <label>Land</label>
              <input name="country" value={form.country} onChange={handleFieldChange} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Geboortedatum</label>
              <IsoDateInput name="birth_date" value={form.birth_date} onChange={handleFieldChange} />
            </div>
            <div className="form-group">
              <label>Overlijdensdatum</label>
              <IsoDateInput name="death_date" value={form.death_date} onChange={handleFieldChange} />
            </div>
          </div>

          <div className="form-group">
            <label>Kwalificaties</label>
            <textarea
              name="kwalificaties"
              rows={3}
              value={form.kwalificaties}
              onChange={handleFieldChange}
              placeholder="Bijv. opleiding, titels (meer regels mogelijk)"
            />
          </div>

          <div className="form-group">
            <label>Korte notitie</label>
            <input name="notitie" value={form.notitie} onChange={handleFieldChange} />
          </div>
          <div className="form-group">
            <label>Notities</label>
            <textarea name="notities" rows={3} value={form.notities} onChange={handleFieldChange} />
          </div>
          <div className="form-group">
            <label>Extra</label>
            <textarea name="extra" rows={2} value={form.extra} onChange={handleFieldChange} />
          </div>

          {error && <p className="form-error">{error}</p>}

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Annuleren
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? "Bezig…" : isEdit ? "Opslaan" : "Aanmaken"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
